import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { permissionService } from '../services/apiServices';
import ErrorDisplay from './ErrorDisplay';
import { sanitizeInput } from '../utils/validationUtils';

const Permissions = () => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPermission, setCurrentPermission] = useState({
    id: null,
    name: ''
  });

  // Fetch permissions
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch permissions
        const response = await permissionService.getPermissions();
        setPermissions(response.data);
      } catch (err) {
        let errorMessage = 'Error loading permissions. Please try again later.';
        
        if (err.response) {
          if (err.response.status === 401) {
            errorMessage = 'Session expired. Please log in again.';
            localStorage.removeItem('authToken');
            window.location.href = '/login';
          } else if (err.response.status === 403) {
            errorMessage = 'Access denied. You do not have permission to view permissions.';
          } else if (err.response.status === 500) {
            // Check if this is a specific access denied error
            if (err.response.data?.message?.includes('Access Denied')) {
              errorMessage = 'Access denied. You do not have permission to view permissions. This feature requires permission management permissions.';
            } else {
              errorMessage = 'Server error. Please try again later.';
            }
          } else if (err.response.data?.message) {
            errorMessage = err.response.data.message;
          }
        } else if (err.request) {
          errorMessage = 'Network error. Please check your connection.';
        } else {
          errorMessage = err.message || 'An unexpected error occurred.';
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleOpenDialog = (permission = null) => {
    if (permission) {
      // Editing existing permission
      setIsEditing(true);
      setCurrentPermission({
        id: permission.id,
        name: permission.name
      });
    } else {
      // Creating new permission
      setIsEditing(false);
      setCurrentPermission({
        id: null,
        name: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentPermission(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (isEditing) {
        // Currently can't update permission name via PUT (based on docs)
        // Just close dialog as name can't be changed
        handleCloseDialog();
      } else {
        // Create new permission
        const response = await permissionService.createPermission({
          name: currentPermission.name
        });
        setPermissions(prev => [...prev, response.data]);
        handleCloseDialog();
      }
    } catch (err) {
      let errorMessage = 'Error saving permission. Please try again.';
      
      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = 'Session expired. Please log in again.';
          localStorage.removeItem('authToken');
          window.location.href = '/login';
        } else if (err.response.status === 403) {
          errorMessage = 'Access denied. You do not have permission to create permissions.';
        } else if (err.response.status === 409) {
          errorMessage = err.response.data?.message || 'A permission with this name already exists.';
        } else if (err.response.status === 500) {
          // Check if this is a specific access denied error
          if (err.response.data?.message?.includes('Access Denied')) {
            errorMessage = 'Access denied. You do not have permission to create permissions. This feature requires permission management permissions.';
          } else {
            errorMessage = 'Server error. Please try again later.';
          }
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = err.message || 'An unexpected error occurred.';
      }
      
      setError(errorMessage);
    }
  };

  const handleDelete = async (permissionId) => {
    if (window.confirm('Are you sure you want to delete this permission? This action cannot be undone.')) {
      try {
        await permissionService.deletePermission(permissionId);
        setPermissions(prev => prev.filter(p => p.id !== permissionId));
      } catch (err) {
        let errorMessage = 'Error deleting permission. Please try again.';
        
        if (err.response) {
          if (err.response.status === 401) {
            errorMessage = 'Session expired. Please log in again.';
            localStorage.removeItem('authToken');
            window.location.href = '/login';
          } else if (err.response.status === 403) {
            errorMessage = 'Access denied. You do not have permission to delete permissions.';
          } else if (err.response.status === 404) {
            errorMessage = 'Permission not found.';
            // Remove permission from local state if it was deleted on the server but not reflected here
            setPermissions(prev => prev.filter(p => p.id !== permissionId));
          } else if (err.response.status === 500) {
            // Check if this is a specific access denied error
            if (err.response.data?.message?.includes('Access Denied')) {
              errorMessage = 'Access denied. You do not have permission to delete permissions. This feature requires permission management permissions.';
            } else {
              errorMessage = 'Server error. Please try again later.';
            }
          } else if (err.response.data?.message) {
            errorMessage = err.response.data.message;
          }
        } else if (err.request) {
          errorMessage = 'Network error. Please check your connection.';
        } else {
          errorMessage = err.message || 'An unexpected error occurred.';
        }
        
        setError(errorMessage);
      }
    }
  };

  if (loading && permissions.length === 0) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Permissions Management
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <ErrorDisplay error={error} onClose={() => setError('')} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Permissions Management
        </Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpenDialog()}>
          Add Permission
        </Button>
      </Box>
      
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {permissions.map((permission) => (
                <TableRow key={permission.id}>
                  <TableCell>{sanitizeInput(permission.id || 'N/A')}</TableCell>
                  <TableCell>{sanitizeInput(permission.name || 'N/A')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Delete Permission">
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => handleDelete(permission.id)}
                        aria-label="delete permission"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog for adding permissions */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditing ? 'Edit Permission' : 'Add New Permission'}
        </DialogTitle>
        <DialogContent>
          {!isEditing && (
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label="Permission Name"
              type="text"
              fullWidth
              variant="outlined"
              value={sanitizeInput(currentPermission.name)}
              onChange={handleInputChange}
              required
              helperText="Permission name cannot be changed after creation"
            />
          )}
          {isEditing && (
            <TextField
              margin="dense"
              name="name"
              label="Permission Name"
              type="text"
              fullWidth
              variant="outlined"
              value={sanitizeInput(currentPermission.name)}
              InputProps={{
                readOnly: true,
              }}
              helperText="Permission name cannot be changed after creation"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {!isEditing && (
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Create
            </Button>
          )}
          {isEditing && (
            <Button onClick={handleCloseDialog} variant="contained" color="primary">
              OK
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Permissions;